/* eslint-disable no-console */

/**
 * Address Validation Cache Utility
 *
 * Wraps any async address validation function with in-memory caching to prevent
 * duplicate billed API calls. Handles:
 * - Repeated clicks on Validate button (same address)
 * - Whitespace-only differences (trim + collapse before fingerprinting)
 * - Failed validation caching (prevents re-submit of known-bad addresses)
 *
 * Usage:
 *   import { withAddressCache } from '../common/addressValidationCache';
 *   const cachedFn = withAddressCache(originalApiFn);
 *   const result = await cachedFn(payload); // uses cache if same address
 */

import {
  AddressDoctorAddress,
  AddressValidationPayload,
  CacheEntry,
} from './addressValidationInterface';

const cache = new Map<string, CacheEntry<unknown>>();

/**
 * Normalize a field value: trim leading/trailing spaces, collapse multiple
 * internal spaces to a single space. Returns '' for null/undefined.
 */
export function normalizeField(value: string | undefined | null): string {
  if (!value) return '';
  return value.trim().replace(/\s+/g, ' ');
}

/**
 * Extract a stable fingerprint from the address validation payload.
 * Covers both Google Address Validation and Address Doctor payload shapes.
 * Returns null if no meaningful address data can be extracted (safe fallback).
 */
export function extractFingerprint(
  payload: AddressValidationPayload
): string | null {
  try {
    const parts: string[] = [];

    // Google Address Validation path
    if (payload?.validateAddress) {
      const va = payload.validateAddress;
      if (Array.isArray(va.addressLines)) {
        parts.push(...va.addressLines.map(normalizeField));
      }
      if (va.postalCode) parts.push(normalizeField(va.postalCode));
      if (va.regionCode) parts.push(normalizeField(va.regionCode));
    }

    // Address Doctor / legacy path
    if (payload?.Addresses && Array.isArray(payload.Addresses)) {
      const addr = payload.Addresses[0];
      if (addr) {
        parts.push(normalizeField(addr.StreetAddress));
        parts.push(normalizeField(addr.City));
        parts.push(normalizeField(addr.StateProvince));
        parts.push(normalizeField(addr.PostalCode));
        parts.push(normalizeField(addr.Country));
      }
    }

    return buildFingerprint(
      parts,
      payload?.LocationNumber ||
        window.sessionStorage.getItem('storeNumber') ||
        ''
    );
  } catch {
    return null;
  }
}

function buildFingerprint(
  parts: string[],
  storeNumber?: string | null
): string | null {
  const normalizedStore = normalizeField(storeNumber);
  if (normalizedStore || parts.some((part) => part.length > 0)) {
    parts.push(normalizedStore);
  }

  if (parts.every((part) => part.length === 0)) {
    return null;
  }

  return parts.join('|').toLowerCase();
}

/**
 * Deep-clone and normalize address fields in the payload before sending
 * to the API. Prevents whitespace-only differences from reaching the backend.
 */
export function normalizePayload(
  payload: AddressValidationPayload
): AddressValidationPayload {
  const p: AddressValidationPayload = JSON.parse(JSON.stringify(payload));

  if (p.validateAddress) {
    if (p.validateAddress.postalCode) {
      p.validateAddress.postalCode = normalizeField(
        p.validateAddress.postalCode
      );
    }
    if (Array.isArray(p.validateAddress.addressLines)) {
      p.validateAddress.addressLines =
        p.validateAddress.addressLines.map(normalizeField);
    }
  }

  if (p.Addresses && Array.isArray(p.Addresses)) {
    p.Addresses = p.Addresses.map((a: AddressDoctorAddress) => ({
      ...a,
      StreetAddress: normalizeField(a.StreetAddress),
      City: normalizeField(a.City),
      StateProvince: normalizeField(a.StateProvince),
      PostalCode: normalizeField(a.PostalCode),
    }));
  }

  return p;
}

/**
 * Higher-order function that wraps an async API call with caching.
 *
 * - Same signature as the original function
 * - If fingerprint matches a cached entry, returns the cached response
 * - If cache miss, calls the original function with a normalized payload
 * - Caches both success and failure responses so a resubmission of the same
 *   (bad) address replays the failure instead of firing another paid call
 * - Falls through to original function if fingerprinting fails
 *
 * @param apiFn - The original async API function (e.g., GetSuggestedAddress)
 * @returns A wrapped function with identical signature
 */
export function withAddressCache<T>(
  apiFn: (payload: AddressValidationPayload) => Promise<T>
): (payload: AddressValidationPayload) => Promise<T> {
  return async (payload: AddressValidationPayload): Promise<T> => {
    const fingerprint = extractFingerprint(payload);

    // If we can't fingerprint, fall through to original (safe)
    if (!fingerprint) {
      return apiFn(payload);
    }

    // Check cache - reuse in-flight promise or resolved result
    const cached = cache.get(fingerprint);
    if (cached) {
      console.log('Address validation cache hit - reusing cached request');
      if (cached.status === 'resolved') {
        return cached.response as T;
      }
      return cached.promise as Promise<T>;
    }

    // Cache miss - call API with normalized payload
    const normalizedPayload = normalizePayload(payload);
    const request = apiFn(normalizedPayload).then(
      (response) => {
        cache.set(fingerprint, {
          status: 'resolved',
          response,
        });
        return response;
      },
      (error) => {
        // On network/API error remove the pending entry so it can be retried
        cache.delete(fingerprint);
        throw error;
      }
    );

    // Store in-flight promise immediately so concurrent callers share it
    cache.set(fingerprint, {
      status: 'pending',
      promise: request,
    });

    return request;
  };
}

/**
 * Check if a given address (as individual fields) already has a cached result.
 * Use this to disable the Validate button when:
 * - The address was already validated (success or failure)
 * - Whitespace-only differences shouldn't re-enable the button
 *
 * @param fields - Record of address field values (will be normalized before checking)
 * @returns true if this address has been validated before (button should be disabled)
 */
export function isAddressAlreadyValidated(
  fields: Record<string, string | undefined | null>
): boolean {
  try {
    const key = buildFingerprint(
      Object.values(fields).map((value) => normalizeField(value)),
      window.sessionStorage.getItem('storeNumber') || ''
    );
    return key ? cache.has(key) : false;
  } catch {
    return false;
  }
}
